import { useLazyQuery } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ExtractionReviewValues } from "components/Document/ExtractionReview";
import { Issue, IssueLevel } from "generated-graphql/graphql";
import { useCallback, useRef, useState } from "react";

import { useApplyExtractedProfile, WASTE_PROFILE_INPUT_VALIDATOR } from "./api";
import {
  reviewedHazardClassIssues,
  toApplyInput,
  toProfileInput,
  toReadingInput,
} from "./extractionReview";

/**
 * Validating and applying a reviewed characterization.
 *
 * `validate` runs the same rules the apply itself enforces, as the reviewer
 * edits, so a contradiction shows up on the offending field rather than as a
 * rejected save. Issue keys already match the grid's, so nothing re-keys them.
 */
export function useApplyReviewedProfile({
  wasteStreamId,
  documentId,
  storedSubsidiaryHazardClass,
  onApplied,
}: {
  wasteStreamId: string;
  documentId: string;
  storedSubsidiaryHazardClass: string | null | undefined;
  onApplied: () => void;
}): {
  validate: (reviewed: ExtractionReviewValues) => Promise<Issue[]>;
  confirm: (reviewed: ExtractionReviewValues) => Promise<void>;
  applying: boolean;
  blockingIssues: string[];
} {
  const alerts = useAlerts();
  const [applyProfile, { loading: applying }] = useApplyExtractedProfile();
  const [runValidator] = useLazyQuery(WASTE_PROFILE_INPUT_VALIDATOR, {
    fetchPolicy: "no-cache",
  });
  const [blockingIssues, setBlockingIssues] = useState<string[]>([]);
  // Two callers run the validator — the grid on every edit, and `confirm` on
  // Apply — and nothing orders their responses. Only the newest may write, or an
  // earlier request resolving late clears the banner that is the live reason
  // Apply refused, leaving a button that did nothing and no explanation.
  const latestValidation = useRef(0);

  const validateReviewedValues = useCallback(
    async (reviewed: ExtractionReviewValues): Promise<Issue[]> => {
      const request = (latestValidation.current += 1);
      const result = await runValidator({
        variables: {
          input: toProfileInput(wasteStreamId, reviewed),
          // The readings too, so the rules about a derived column — every
          // flash-point and density rule — judge the profile this review would
          // write rather than one with those columns missing. Without it a
          // reviewer who marks a waste ignitable and states a flash-point band is
          // told the flash point is absent, and no field on screen fixes it.
          asRead: toReadingInput(reviewed),
        },
      });
      if (result.error) throw result.error;
      const issues = [
        ...(result.data?.wasteProfileInputValidator ?? []),
        ...reviewedHazardClassIssues(reviewed, storedSubsidiaryHazardClass),
      ];
      // The grid runs this as the reviewer edits, so it is also what tells us the
      // banner is out of date. Without this it keeps naming a problem they have
      // already fixed until they click Apply again — and the banner is the thing
      // explaining why Apply did nothing.
      if (
        request === latestValidation.current &&
        !issues.some((issue) => issue.level === IssueLevel.Critical)
      ) {
        setBlockingIssues([]);
      }
      return issues;
    },
    [runValidator, wasteStreamId, storedSubsidiaryHazardClass]
  );

  // Form validation must resolve with issues; Apply separately handles request failures.
  const validate = useCallback(
    async (reviewed: ExtractionReviewValues): Promise<Issue[]> => {
      try {
        return await validateReviewedValues(reviewed);
      } catch {
        return [
          {
            key: "validation",
            modelName: "WasteProfile",
            level: IssueLevel.Critical,
            message: "Could not validate the characterization. Try again before applying.",
          },
        ];
      }
    },
    [validateReviewedValues]
  );

  const confirm = useCallback(
    async (reviewed: ExtractionReviewValues) => {
      // Re-validated here rather than trusting the grid's inline pass, which is
      // not a gate: `useValidatingForm` passes an `onInvalid` handler that calls
      // the submit handler anyway, so a form it has marked invalid still commits.
      //
      // CRITICAL only, matching what `applyExtractedProfile` itself refuses. The
      // engine's own split is that CRITICAL means the write would fail and ERROR
      // means save it and resolve it later, so blocking on ERROR both contradicts
      // the rule that raised it and hands the reviewer a wall on a profile the
      // mutation would have accepted. An ERROR still shows inline on the offending
      // field and in the grid's issue list — it just no longer refuses.
      let issues: Issue[];
      try {
        issues = await validateReviewedValues(reviewed);
      } catch (error) {
        // A validator that failed to answer is not a validator that approved.
        alerts.error(
          "Could not validate the characterization",
          error instanceof Error ? error.message : undefined
        );
        return;
      }
      const blocking = issues.filter((issue) => issue.level === IssueLevel.Critical);
      if (blocking.length > 0) {
        setBlockingIssues(blocking.map((issue) => issue.message));
        return;
      }
      // Unconditionally, unlike the sequence-guarded clear inside `validate`. A
      // newer validation still in flight is a reason to distrust *its* result,
      // not the one this decision was just made on — and if the mutation below
      // fails, the dialog stays open with the banner still naming a problem the
      // reviewer has already fixed.
      setBlockingIssues([]);

      try {
        await applyProfile({
          variables: { input: toApplyInput(wasteStreamId, documentId, reviewed) },
        });
        alerts.success("Characterization applied to the profile");
        onApplied();
      } catch (error) {
        alerts.error(
          "Could not apply the characterization",
          error instanceof Error ? error.message : undefined
        );
      }
    },
    [alerts, applyProfile, documentId, onApplied, validateReviewedValues, wasteStreamId]
  );

  return { validate, confirm, applying, blockingIssues };
}
